import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import type { RiskScoreHistoryRow } from '@risksmart-app/drizzle/src/types';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UNRATED } from 'src/pages/controls/lookupData';

import { useAggregation } from '@/hooks/useAggregation';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import { RiskScoreBadge } from '../../../RiskScoreBadge';

/**
 * `risk_score_audit` rows carry no primary key through the score-history
 * endpoint, so a synthetic `id` is added for table `trackBy`. It combines the
 * ordered index with `ModifiedAtTimestamp` so a server-side history-cap
 * eviction (which shifts indices) still yields a distinct key per row rather
 * than silently reusing an evicted row's index. Rows arrive oldest → newest.
 */
export type RiskScoreHistoryTableRow = RiskScoreHistoryRow & { id: string };

export const useGetRiskScoreHistoryTableProps = (
  rows: RiskScoreHistoryRow[]
) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'scoreHistory' });
  const { t: ct } = useTranslation(['common'], {
    keyPrefix: 'scoreHistory.table.columns',
  });

  // Same resolver/model the risk register uses, so score/rating cells render
  // the identical coloured badges. Config versions are not tracked per audit
  // row, so ratings resolve against the latest config (configId omitted).
  const { riskModel } = useAggregation();
  const { resolveRiskRating } = useRiskRatingResolver('standard', {
    configType: 'risk_assessment',
  });

  const resolveRating = (
    controlType: Risk_Assessment_Result_Control_Type_Enum,
    likelihood: number | null,
    impact: number | null,
    rating: number | null
  ) =>
    resolveRiskRating({ likelihood, impact, controlType, rating }) ?? UNRATED;

  const fields: TableFields<RiskScoreHistoryTableRow> = {
    ModifiedAtTimestamp: dateColumnFromConfig({
      header: { header: ct('timestamp') },
      dateField: 'ModifiedAtTimestamp',
      includeTime: true,
    }),
    ResidualScore: {
      header: ct('residualScore'),
      cell: (item) => (
        <RiskScoreBadge
          controlType={Risk_Assessment_Result_Control_Type_Enum.Controlled}
          riskScoreModel={riskModel}
          impact={item.ResidualImpact}
          likelihood={item.ResidualLikelihood}
          score={item.ResidualScore}
          rating={item.ResidualRating}
        />
      ),
      exportVal: (item) => item.ResidualScore?.toFixed(1) ?? '',
    },
    InherentScore: {
      header: ct('inherentScore'),
      cell: (item) => (
        <RiskScoreBadge
          controlType={Risk_Assessment_Result_Control_Type_Enum.Uncontrolled}
          riskScoreModel={riskModel}
          impact={item.InherentImpact}
          likelihood={item.InherentLikelihood}
          score={item.InherentScore}
          rating={item.InherentRating}
        />
      ),
      exportVal: (item) => item.InherentScore?.toFixed(1) ?? '',
    },
    ResidualRating: {
      header: ct('residualRating'),
      cell: (item) => (
        <SimpleRatingBadge
          rating={resolveRating(
            Risk_Assessment_Result_Control_Type_Enum.Controlled,
            item.ResidualLikelihood,
            item.ResidualImpact,
            item.ResidualRating
          )}
        />
      ),
      exportVal: (item) =>
        resolveRating(
          Risk_Assessment_Result_Control_Type_Enum.Controlled,
          item.ResidualLikelihood,
          item.ResidualImpact,
          item.ResidualRating
        ).label,
    },
    InherentRating: {
      header: ct('inherentRating'),
      cell: (item) => (
        <SimpleRatingBadge
          rating={resolveRating(
            Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
            item.InherentLikelihood,
            item.InherentImpact,
            item.InherentRating
          )}
        />
      ),
      exportVal: (item) =>
        resolveRating(
          Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
          item.InherentLikelihood,
          item.InherentImpact,
          item.InherentRating
        ).label,
    },
  };

  const mappedData = useMemo<RiskScoreHistoryTableRow[]>(
    () =>
      rows.map((row, index) => ({
        ...row,
        id: `${index}-${row.ModifiedAtTimestamp}`,
      })),
    [rows]
  );

  return useGetTableProps({
    tableId: 'riskScoreHistory',
    data: mappedData,
    entityLabel: t('table.title'),
    fields,
    initialColumns: [
      'ModifiedAtTimestamp',
      'ResidualScore',
      'InherentScore',
      'ResidualRating',
      'InherentRating',
    ],
    preferencesStorageKey: 'RiskScoreHistoryTable-Preferences',
    customAttributeFormIds: [],
  });
};
